import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import {
  ArrowLeft,
  Eye,
  FileSpreadsheet,
  Inbox,
  Loader2,
  Printer,
  Send,
  Undo2,
} from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { Button, buttonVariants } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatNumber, formatTaka } from '@/utils/number';
import { PcmpHoldActionButtons } from '../components/PcmpHoldActions/PcmpHoldActionButtons';
import { PCMP_PERMISSION } from '../api/pcmpAccess.api';
import { downloadPcmpMisBatchXlsx, pcmpMisBatchQueryOptions } from '../api/pcmpMis.api';
import { PcmpBatchStepCell } from '../components/PcmpBatch/PcmpBatchCells';
import { PcmpBatchStatusPill } from '../components/PcmpMisBatches/PcmpMisBatchCells';
import { PcmpMisPublishDialog } from '../components/PcmpMisBatches/PcmpMisPublishDialog';
import {
  PcmpMisRollbackDialog,
  type PcmpMisRollbackTarget,
} from '../components/PcmpMisBatches/PcmpMisRollbackDialog';
import { PcmpSelectionBar } from '../components/PcmpBatch/PcmpSelectionBar';
import { getPcmpMisColumns } from '../components/PcmpMisQueue/PcmpMisColumns';
import { PcmpQueueFilterBar } from '../components/PcmpQueue/PcmpQueueFilterBar';
import { pcmpSelectColumn } from '../components/PcmpQueue/pcmpSelectColumn';
import { PCMP_CHANNEL_LABELS } from '../constants/pcmpProposal.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../constants/pcmpQueue.constants';
import { useDownloadPcmpBatchXlsx } from '../hooks/useDownloadPcmpBatchXlsx';
import { usePcmpMisBatchFilters } from '../hooks/usePcmpMisBatchFilters';
import { usePcmpPermissions } from '../hooks/usePcmpPermissions';
import { usePcmpRowSelection } from '../hooks/usePcmpRowSelection';
import type { PcmpMisBatchItem } from '../types/pcmpMis.types';

const rollbackBlockedReason = (item: PcmpMisBatchItem) =>
  item.canRollback ? null : (item.rollbackBlockedReason ?? 'Cannot be rolled back.');

/** One routing batch: its facts, members, publish, rollback and exports. */
export function PcmpMisBatchPage() {
  const router = useRouter();
  const controller = usePcmpMisBatchFilters();
  const { batchNo, params, appliedFilters, showAll, limit } = controller;
  const { can } = usePcmpPermissions();
  const canAct = can(PCMP_PERMISSION.MIS, PCMP_PERMISSION.ACT);
  const canExport = can(PCMP_PERMISSION.VIEW_ALL, PCMP_PERMISSION.EXPORT);

  const [publishing, setPublishing] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<PcmpMisRollbackTarget | null>(null);
  const selection = usePcmpRowSelection(JSON.stringify([batchNo, appliedFilters, showAll]));
  const xlsx = useDownloadPcmpBatchXlsx((date) => downloadPcmpMisBatchXlsx(batchNo, date));

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpMisBatchQueryOptions(batchNo, params),
  );

  useKeepPageInRange(params.page, data?.proposals.meta.totalPages, controller.setPage);

  const rows = useMemo(() => data?.proposals.items ?? [], [data]);
  const batch = data?.batch;
  const isStaged = batch?.status === 'STAGED';
  const canRollback = (isStaged || data?.batchHasRollbackItems) && canAct;

  const openProposal = useCallback(
    (item: PcmpMisBatchItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const backButton = (
    <Link
      to="/pcmp/mis"
      search={{ tab: 'BATCHES' }}
      className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-fit gap-1.5')}
    >
      <ArrowLeft className="size-3.5" />
      Batches
    </Link>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <PageLoader />
      </div>
    );
  }

  if (error || !data || !batch) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <ErrorState
          error={error}
          fallbackTitle={`Could not load batch #${batchNo}`}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  const columns = getPcmpMisColumns<PcmpMisBatchItem>({
    leading: canRollback
      ? [pcmpSelectColumn<PcmpMisBatchItem>({ selection, blockedReason: rollbackBlockedReason })]
      : [],
    renderActions: (item) => (
      <>
        <PcmpHoldActionButtons
          proposalId={item.proposalId}
          refNo={item.refNo}
          canHold={item.canHold}
          canRelease={item.canRelease}
          hold={item.hold}
          className="flex-nowrap"
        />
        <Link
          to="/pcmp/proposals/$proposalId"
          params={{ proposalId: item.proposalId }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
        >
          <Eye className="size-3.5" />
          View
        </Link>
      </>
    ),
  });

  // Rollback picks rows one by one (legacy): no "select all matching".
  const summary = selection.summarize(0, 0);
  const refNoOf = (proposalId: number) => rows.find((row) => row.proposalId === proposalId)?.refNo;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {backButton}
          <h1 className="text-2xl font-semibold">Batch #{batch.batchNo}</h1>
          <PcmpBatchStatusPill status={batch.status} />
          {batch.channel ? (
            <span className="text-sm text-muted-foreground">
              {PCMP_CHANNEL_LABELS[batch.channel]} pays
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canExport ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => xlsx.mutate()}
                disabled={xlsx.isPending}
                className="gap-1.5"
              >
                {xlsx.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <FileSpreadsheet className="size-3.5" />
                )}
                Excel
              </Button>
              <Link
                to="/pcmp/mis/batches/$batchNo/print"
                params={{ batchNo }}
                target="_blank"
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
              >
                <Printer className="size-3.5" />
                Print / PDF
              </Link>
            </>
          ) : null}
          {isStaged && canAct ? (
            <Button
              type="button"
              size="sm"
              onClick={() => setPublishing(true)}
              disabled={batch.itemCount === 0}
              className="gap-1.5"
            >
              <Send className="size-3.5" />
              Publish
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="flex flex-col rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="text-xs text-muted-foreground">Proposals</span>
          <span className="text-lg font-semibold tabular-nums">
            {formatNumber(batch.itemCount)}
          </span>
        </div>
        <div className="flex flex-col rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="text-xs text-muted-foreground">Amount</span>
          <span className="text-lg font-semibold tabular-nums">
            {formatTaka(batch.totalAmount)}
          </span>
        </div>
        <div className="flex flex-col gap-1 rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="text-xs text-muted-foreground">Staged</span>
          <PcmpBatchStepCell by={batch.stagedBy} at={batch.stagedAt} />
        </div>
        <div className="flex flex-col gap-1 rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="text-xs text-muted-foreground">Published</span>
          <PcmpBatchStepCell by={batch.publishedBy} at={batch.publishedAt} />
          {batch.publishComment ? (
            <span className="text-xs whitespace-pre-line text-muted-foreground">
              “{batch.publishComment}”
            </span>
          ) : null}
        </div>
      </div>

      <PcmpQueueFilterBar controller={controller} idPrefix="pcmp-batch" />

      {data.isFiltered || showAll ? (
        <div className="flex items-center gap-2 text-sm">
          <Checkbox
            id="pcmp-batch-show-all"
            checked={showAll}
            onCheckedChange={(checked) => controller.setShowAll(checked)}
          />
          <Label htmlFor="pcmp-batch-show-all" className="font-normal">
            Show all {formatNumber(batch.itemCount)} proposals in the batch, not only the{' '}
            {formatNumber(batch.matchedItemCount ?? data.proposals.meta.total)} matching the filters
          </Label>
        </div>
      ) : null}

      {canRollback ? (
        <PcmpSelectionBar
          selection={selection}
          summary={summary}
          hint={
            isStaged
              ? 'Tick staged proposals to roll them back to Pre-Batch.'
              : 'Tick published proposals to roll them back to Pre-Batch.'
          }
        >
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setRollbackTarget({
                batchNo,
                channel: batch.channel,
                proposalIds: selection.pickedIds,
                amount: summary.amount,
              })
            }
            disabled={summary.count === 0}
            className="gap-1.5 text-destructive hover:text-destructive"
          >
            <Undo2 className="size-3.5" />
            Roll back
          </Button>
        </PcmpSelectionBar>
      ) : null}

      <DataTable
        data={rows}
        columns={columns}
        isLoading={false}
        stickyFirstColumn
        className={cn(isFetching && 'opacity-70')}
        emptyIcon={Inbox}
        emptyMessage={
          data.isFiltered ? 'No proposals in this batch match the filters' : 'This batch is empty'
        }
        emptyHint={
          data.isFiltered
            ? 'Show all proposals in the batch, or change the filters.'
            : 'Every proposal in it was rolled back to Pre-Batch.'
        }
        skeletonRowCount={Math.min(limit, 15)}
        getRowClassName={(item) =>
          canRollback && selection.isSelected(item.proposalId) ? 'bg-primary/5' : undefined
        }
        onRowClick={openProposal}
      />

      <DataTablePagination
        page={data.proposals.meta.page}
        totalPages={data.proposals.meta.totalPages}
        total={data.proposals.meta.total}
        limit={data.proposals.meta.limit}
        onPageChange={controller.setPage}
        limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
      />

      {publishing ? (
        <PcmpMisPublishDialog batch={batch} onClose={() => setPublishing(false)} />
      ) : null}
      {rollbackTarget ? (
        <PcmpMisRollbackDialog
          target={rollbackTarget}
          refNoOf={refNoOf}
          onRolledBack={selection.clear}
          onClose={() => setRollbackTarget(null)}
        />
      ) : null}
    </div>
  );
}
